/**
 * FeatureSectionBlock Component
 *
 * Custom BlockNote React component for feature sections with icon support
 * and flexible layouts for marketing pages.
 *
 * Features:
 * - Small title (optional category/context)
 * - Main title (required)
 * - Description (required)
 * - Features list (1-12 items) with drag-and-drop reordering
 * - Icon selection per feature (library + custom SVG)
 * - Featured image/video
 * - Background image/video with overlay controls
 * - 4 layout variations:
 *   - image-right: Features grid left / Image right
 *   - image-left: Features grid right / Image left
 *   - image-below: Features grid above image
 *   - no-image: Features grid only (full width)
 * - Edit mode with collapsible sections
 * - View mode with responsive grid rendering
 *
 * Props (from block.props):
 * - smallTitle: Optional category text (max 100 chars)
 * - title: Main headline (required, max 200 chars)
 * - description: Supporting text (required, max 500 chars)
 * - features: Array of feature objects [{ icon, title, description }] (1-12)
 * - featuredMedia: { type, url, alt } for main image/video
 * - backgroundMedia: { type, url, overlay } for background
 * - layout: "image-right"|"image-left"|"image-below"|"no-image"
 *
 * Related Files:
 * - app/components/blocknote/blocks/feature_section_component.rb (SSR)
 * - app/javascript/components/blocknote/marketing/features/IconSelector.jsx
 */

import React, { useState } from "react";
import MediaSelector from "./marketing/shared/MediaSelector.jsx";
import LayoutSelector from "./marketing/shared/LayoutSelector.jsx";
import OverlayControls from "./marketing/shared/OverlayControls.jsx";
import IconSelector from "./marketing/features/IconSelector.jsx";

// Layout options for Feature Section
const LAYOUT_OPTIONS = [
  {
    value: "image-right",
    label: "Image Right",
    description: "Features left, image right",
  },
  {
    value: "image-left",
    label: "Image Left",
    description: "Image left, features right",
  },
  {
    value: "image-below",
    label: "Image Below",
    description: "Features above image",
  },
  {
    value: "no-image",
    label: "No Image",
    description: "Features only, full width",
  },
];

const DEFAULT_FEATURE = {
  icon: null,
  title: "",
  description: "",
};

export default function FeatureSectionBlock({ block, editor }) {
  const [isEditMode, setIsEditMode] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    content: true,
    features: true,
    media: false,
    background: false,
    layout: true,
  });

  // Get current props with defaults
  const props = block.props || {};
  const smallTitle = props.smallTitle || "";
  const title = props.title || "";
  const description = props.description || "";
  const features = props.features || [];
  const featuredMedia = props.featuredMedia || null;
  const backgroundMedia = props.backgroundMedia || null;
  const layout = props.layout || "image-right";

  // Update a specific prop
  const updateProp = (key, value) => {
    editor.updateBlock(block, {
      props: {
        ...props,
        [key]: value,
      },
    });
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  // Feature list management
  const handleAddFeature = () => {
    if (features.length < 12) {
      updateProp("features", [...features, { ...DEFAULT_FEATURE }]);
    }
  };

  const handleRemoveFeature = (index) => {
    const updatedFeatures = features.filter((_, i) => i !== index);
    updateProp("features", updatedFeatures);
  };

  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...features];
    updatedFeatures[index] = {
      ...updatedFeatures[index],
      [field]: value,
    };
    updateProp("features", updatedFeatures);
  };

  // Simple drag-and-drop handlers (basic implementation)
  const handleFeatureMoveUp = (index) => {
    if (index === 0) return;
    const updatedFeatures = [...features];
    [updatedFeatures[index - 1], updatedFeatures[index]] = [
      updatedFeatures[index],
      updatedFeatures[index - 1],
    ];
    updateProp("features", updatedFeatures);
  };

  const handleFeatureMoveDown = (index) => {
    if (index === features.length - 1) return;
    const updatedFeatures = [...features];
    [updatedFeatures[index], updatedFeatures[index + 1]] = [
      updatedFeatures[index + 1],
      updatedFeatures[index],
    ];
    updateProp("features", updatedFeatures);
  };

  // Render collapsible section header
  const renderSectionHeader = (title, sectionKey, required = false) => (
    <button
      type="button"
      onClick={() => toggleSection(sectionKey)}
      className="flex items-center justify-between w-full p-3 bg-gray-100 hover:bg-gray-200 rounded-t transition-colors"
    >
      <span className="font-medium text-neutral-900">
        {title}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      <svg
        className={`w-5 h-5 text-neutral-600 transition-transform ${
          expandedSections[sectionKey] ? "transform rotate-180" : ""
        }`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
  );

  // Edit Mode UI
  if (isEditMode) {
    return (
      <div className="feature-section-block border-2 border-green-200 rounded-lg p-4 bg-white my-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b">
          <div className="flex items-center gap-2">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
              />
            </svg>
            <span className="font-semibold text-neutral-900">
              Feature Section
            </span>
          </div>
          <button
            type="button"
            onClick={() => setIsEditMode(false)}
            className="text-sm text-green-600 hover:text-green-800"
          >
            Preview
          </button>
        </div>

        {/* Content Section */}
        <div className="mb-4">
          {renderSectionHeader("Content", "content", true)}
          {expandedSections.content && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4 space-y-3">
              {/* Small Title */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Small Title{" "}
                  <span className="text-neutral-500 text-xs">(optional)</span>
                </label>
                <input
                  type="text"
                  value={smallTitle}
                  onChange={(e) => updateProp("smallTitle", e.target.value)}
                  placeholder="e.g., FEATURES"
                  className="form-control"
                  maxLength={100}
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Main Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => updateProp("title", e.target.value)}
                  placeholder="Enter section headline"
                  className="form-control"
                  maxLength={200}
                  required
                />
                <div className="text-xs text-neutral-500 mt-1">
                  {title.length}/200
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => updateProp("description", e.target.value)}
                  placeholder="Enter section description"
                  className="form-control"
                  rows={3}
                  maxLength={500}
                  required
                />
                <div className="text-xs text-neutral-500 mt-1">
                  {description.length}/500
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Features List Section */}
        <div className="mb-4">
          {renderSectionHeader(
            `Features (${features.length}/12)`,
            "features",
            true,
          )}
          {expandedSections.features && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4 space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="border rounded p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-neutral-700">
                      Feature {index + 1}
                    </span>
                    <div className="flex items-center gap-2">
                      {/* Move up */}
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleFeatureMoveUp(index)}
                          className="text-neutral-600 hover:text-neutral-800"
                          title="Move up"
                        >
                          ↑
                        </button>
                      )}
                      {/* Move down */}
                      {index < features.length - 1 && (
                        <button
                          type="button"
                          onClick={() => handleFeatureMoveDown(index)}
                          className="text-neutral-600 hover:text-neutral-800"
                          title="Move down"
                        >
                          ↓
                        </button>
                      )}
                      {/* Remove */}
                      <button
                        type="button"
                        onClick={() => handleRemoveFeature(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {/* Icon */}
                  <IconSelector
                    value={feature.icon}
                    onChange={(icon) =>
                      handleFeatureChange(index, "icon", icon)
                    }
                    label="Icon (optional)"
                  />

                  {/* Feature title */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Feature Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={feature.title || ""}
                      onChange={(e) =>
                        handleFeatureChange(index, "title", e.target.value)
                      }
                      placeholder="Feature headline"
                      className="form-control"
                      maxLength={100}
                      required
                    />
                  </div>

                  {/* Feature description */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Feature Description{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={feature.description || ""}
                      onChange={(e) =>
                        handleFeatureChange(
                          index,
                          "description",
                          e.target.value,
                        )
                      }
                      placeholder="Feature details"
                      className="form-control"
                      rows={2}
                      maxLength={300}
                      required
                    />
                  </div>
                </div>
              ))}

              {/* Add feature button */}
              {features.length < 12 && (
                <button
                  type="button"
                  onClick={handleAddFeature}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded text-sm text-neutral-600 hover:border-gray-400 hover:text-neutral-700 transition-colors"
                >
                  + Add Feature ({12 - features.length} remaining)
                </button>
              )}
            </div>
          )}
        </div>

        {/* Featured Media Section */}
        <div className="mb-4">
          {renderSectionHeader("Featured Media (Optional)", "media")}
          {expandedSections.media && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4">
              <MediaSelector
                value={featuredMedia}
                onChange={(media) => updateProp("featuredMedia", media)}
                label="Featured Image or Video"
                allowVideo={true}
                allowYoutube={false}
              />
            </div>
          )}
        </div>

        {/* Background Media Section */}
        <div className="mb-4">
          {renderSectionHeader("Background (Optional)", "background")}
          {expandedSections.background && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4 space-y-4">
              <MediaSelector
                value={backgroundMedia}
                onChange={(media) => updateProp("backgroundMedia", media)}
                label="Background Image or Video"
                allowVideo={true}
                allowYoutube={false}
              />

              {backgroundMedia && backgroundMedia.url && (
                <OverlayControls
                  value={backgroundMedia.overlay || {}}
                  onChange={(overlay) =>
                    updateProp("backgroundMedia", {
                      ...backgroundMedia,
                      overlay,
                    })
                  }
                  showPreview={true}
                />
              )}
            </div>
          )}
        </div>

        {/* Layout Section */}
        <div className="mb-4">
          {renderSectionHeader("Layout", "layout")}
          {expandedSections.layout && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4">
              <LayoutSelector
                value={layout}
                onChange={(newLayout) => updateProp("layout", newLayout)}
                layouts={LAYOUT_OPTIONS}
                blockType="feature"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // View Mode (Preview)
  return (
    <div className="feature-section-block border border-gray-300 rounded-lg p-6 bg-white my-4">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-neutral-600">
          Feature Section Preview
        </span>
        <button
          type="button"
          onClick={() => setIsEditMode(true)}
          className="text-sm text-green-600 hover:text-green-800"
        >
          Edit
        </button>
      </div>

      <div className="space-y-6">
        {smallTitle && (
          <div className="text-sm font-semibold text-green-600 uppercase tracking-wide">
            {smallTitle}
          </div>
        )}

        {title && (
          <h2 className="text-3xl font-bold text-neutral-900">{title}</h2>
        )}

        {description && (
          <p className="text-lg text-neutral-600">{description}</p>
        )}

        {features && features.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {features.map((feature, index) => (
              <div key={index} className="space-y-2">
                {feature.icon && (
                  <div className="w-10 h-10 text-green-600">📌</div>
                )}
                <h3 className="font-semibold text-neutral-900">
                  {feature.title}
                </h3>
                <p className="text-sm text-neutral-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {featuredMedia && featuredMedia.url && (
          <div className="mt-6">
            {featuredMedia.type === "image" ? (
              <img
                src={featuredMedia.url}
                alt={featuredMedia.alt || ""}
                className="max-w-full h-auto rounded"
              />
            ) : (
              <div className="text-sm text-neutral-500 p-4 border rounded">
                Video: {featuredMedia.url}
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-neutral-500 mt-4 pt-4 border-t">
          Layout: {layout} • {features.length} features • SSR rendering will
          apply full responsive styles
        </div>
      </div>
    </div>
  );
}
